/**
 * L'atelier graphique — l'ÉTAT de l'atelier et son réducteur : annuler,
 * rétablir, glisser (doc 26, § 1.3).
 *
 * L'historique vit ici, pas dans le document envoyé : une réponse ou un
 * brouillon est l'état courant, rien de plus.
 *
 * ── Un invariant ───────────────────────────────────────────────────────────
 *
 * Le document est TOUJOURS l'historique rejoué. Le glisser en cours modifie
 * le document sans commande (chaque image du geste) ; toute autre action qui
 * touche l'historique (commande, annuler, rétablir, outil) clôt d'abord le
 * geste, comme un lâcher, en UNE commande `deplacer`.
 *
 * Réducteur pur, sans React, sans horloge ni hasard : un identifiant neuf se
 * tire de l'état (`numeroLibre`), jamais d'un compteur caché.
 */
import { avecDependants, } from './construction.js';
import { tousFinis } from './geometrie.js';
/** Commandes gardées dans `passe`, au plus. */
export const HISTORIQUE_MAX = 200;
/** Les définitions qu'un déplacement peut toucher (§ 1.3) ; tout autre objet suit ses points. */
const DEPLACABLES = new Set(['point_libre', 'point_sur']);
/** L'état à l'ouverture de l'atelier : historique vide, rien de sélectionné. */
export function etatInitial(document, outil = 'choisir') {
    return { document, passe: [], futur: [], outil, selection: [], geste: null };
}
/* ================================================================ identifiants */
const numeroDe = (id) => {
    const m = /(\d+)$/.exec(id);
    return m === null ? 0 : Number(m[1]);
};
function idsDeCommande(c) {
    switch (c.type) {
        case 'ajouter':
        case 'partir_de_l_enonce':
            return c.objets.map((o) => o.id);
        case 'supprimer':
            return c.objets.map((o) => o.objet.id);
        case 'deplacer':
        case 'modifier':
            return [c.id];
    }
}
/**
 * Le premier numéro jamais employé par le document NI par son historique :
 * un identifiant n'est jamais réutilisé, même après une suppression qu'on
 * pourrait annuler.
 */
export function numeroLibre(e) {
    let max = 0;
    for (const o of e.document.objets)
        max = Math.max(max, numeroDe(o.id));
    for (const c of [...e.passe, ...e.futur]) {
        for (const id of idsDeCommande(c))
            max = Math.max(max, numeroDe(id));
    }
    return max + 1;
}
/** Un identifiant neuf : « p7 », « d7 »… (`prefixe` : des minuscules). */
export function prochainId(e, prefixe) {
    return `${prefixe}${numeroLibre(e)}`;
}
/* ================================================================== commandes */
/** La commande qui supprime ces objets ET leurs dépendants, chacun à son rang ; `null` si rien n'est à supprimer. */
export function commandeSupprimer(doc, ids) {
    const touches = avecDependants(doc, ids);
    const objets = doc.objets
        .map((objet, rang) => ({ rang, objet }))
        .filter((x) => touches.has(x.objet.id));
    return objets.length === 0 ? null : { type: 'supprimer', objets };
}
const avecObjets = (doc, objets) => ({ ...doc, objets });
function remplacer(doc, id, f) {
    const i = doc.objets.findIndex((o) => o.id === id);
    if (i < 0)
        return null;
    const objets = [...doc.objets];
    objets[i] = f(objets[i]);
    return avecObjets(doc, objets);
}
/** Applique une commande ; `null` si elle ne s'applique pas (id déjà pris ou absent, objet non déplaçable). */
export function appliquerCommande(doc, c) {
    switch (c.type) {
        case 'ajouter':
        case 'partir_de_l_enonce': {
            if (c.objets.length === 0)
                return null;
            const pris = new Set(doc.objets.map((o) => o.id));
            for (const o of c.objets) {
                if (pris.has(o.id))
                    return null;
                pris.add(o.id);
            }
            return avecObjets(doc, [...doc.objets, ...c.objets]);
        }
        case 'deplacer': {
            const o = doc.objets.find((x) => x.id === c.id);
            if (o === undefined || !DEPLACABLES.has(o.def.type) || c.apres.type !== o.def.type) {
                return null;
            }
            return remplacer(doc, c.id, (x) => ({ ...x, def: c.apres }));
        }
        case 'modifier':
            return remplacer(doc, c.id, () => ({ ...c.apres, id: c.id }));
        case 'supprimer': {
            const ids = new Set(c.objets.map((o) => o.objet.id));
            const objets = doc.objets.filter((o) => !ids.has(o.id));
            return objets.length === doc.objets.length ? null : avecObjets(doc, objets);
        }
    }
}
/** Défait une commande appliquée : le document d'avant. */
export function defaireCommande(doc, c) {
    switch (c.type) {
        case 'ajouter':
        case 'partir_de_l_enonce': {
            const ids = new Set(c.objets.map((o) => o.id));
            return avecObjets(doc, doc.objets.filter((o) => !ids.has(o.id)));
        }
        case 'deplacer':
            return remplacer(doc, c.id, (x) => ({ ...x, def: c.avant })) ?? doc;
        case 'modifier':
            return remplacer(doc, c.id, () => ({ ...c.avant, id: c.id })) ?? doc;
        case 'supprimer': {
            // Chacun à son rang d'origine, du plus petit au plus grand.
            const objets = [...doc.objets];
            for (const { rang, objet } of [...c.objets].sort((a, b) => a.rang - b.rang)) {
                objets.splice(Math.min(rang, objets.length), 0, objet);
            }
            return avecObjets(doc, objets);
        }
    }
}
/* ================================================================== réducteur */
const borne = (passe) => passe.length > HISTORIQUE_MAX ? passe.slice(passe.length - HISTORIQUE_MAX) : passe;
/** La sélection, sans les objets qui ne sont plus dans le document. */
function selectionValide(doc, selection) {
    if (selection.length === 0)
        return selection;
    const ids = new Set(doc.objets.map((o) => o.id));
    const garde = selection.filter((id) => ids.has(id));
    return garde.length === selection.length ? selection : garde;
}
const memeDefinition = (a, b) => JSON.stringify(a) === JSON.stringify(b);
/** Clôt le geste en cours : UNE commande `deplacer`, s'il a déplacé quelque chose. */
function clore(e) {
    if (e.geste === null)
        return e;
    const { id, avant } = e.geste;
    const o = e.document.objets.find((x) => x.id === id);
    if (o === undefined || memeDefinition(o.def, avant))
        return { ...e, geste: null };
    const c = { type: 'deplacer', id, avant, apres: o.def };
    return { ...e, geste: null, passe: borne([...e.passe, c]), futur: [] };
}
function executer(e, c) {
    const document = appliquerCommande(e.document, c);
    if (document === null)
        return e;
    return {
        ...e,
        document,
        passe: borne([...e.passe, c]),
        futur: [],
        selection: selectionValide(document, e.selection),
    };
}
/** Le réducteur de l'atelier : pur, sans React (§ 1.3). */
export function reduireAtelier(e, a) {
    switch (a.type) {
        case 'commande':
            return executer(clore(e), a.commande);
        case 'annuler': {
            const e1 = clore(e);
            const c = e1.passe[e1.passe.length - 1];
            if (c === undefined)
                return e1;
            const document = defaireCommande(e1.document, c);
            return {
                ...e1,
                document,
                passe: e1.passe.slice(0, -1),
                futur: [c, ...e1.futur],
                selection: selectionValide(document, e1.selection),
            };
        }
        case 'retablir': {
            const e1 = clore(e);
            const c = e1.futur[0];
            if (c === undefined)
                return e1;
            const document = appliquerCommande(e1.document, c);
            if (document === null)
                return e1;
            return {
                ...e1,
                document,
                passe: borne([...e1.passe, c]),
                futur: e1.futur.slice(1),
                selection: selectionValide(document, e1.selection),
            };
        }
        case 'glisser': {
            const e1 = e.geste !== null && e.geste.id !== a.id ? clore(e) : e;
            const o = e1.document.objets.find((x) => x.id === a.id);
            // Phase 1 : seul un point libre se glisse (un point sur un objet se déplace par `t`, phase 2).
            if (o === undefined || o.def.type !== 'point_libre' || !tousFinis(a.vers.x, a.vers.y)) {
                return e1;
            }
            const geste = e1.geste ?? { id: a.id, avant: o.def };
            const document = remplacer(e1.document, a.id, (x) => ({
                ...x,
                def: { type: 'point_libre', x: a.vers.x, y: a.vers.y },
            }));
            return document === null ? e1 : { ...e1, document, geste };
        }
        case 'lacher':
            return clore(e);
        case 'outil':
            return { ...clore(e), outil: a.outil };
        case 'calque':
            return {
                ...e,
                document: { ...e.document, calque: { ...e.document.calque, visible: a.visible } },
            };
        case 'selectionner':
            return { ...e, selection: selectionValide(e.document, a.ids) };
    }
}
//# sourceMappingURL=construction-reducteur.js.map